/**
 * Le valute offerte dal configuratore del campo Valuta.
 *
 * Il contratto trasporta gia' `currencyCode` e `symbol`: qui c'e' solo la
 * tabella che li tiene insieme, cosi' il simbolo non va piu' scritto a mano e
 * non puo' contraddire il codice scelto. `symbol` resta il valore mostrato
 * accanto all'importo; per le valute il cui simbolo coincide con il codice
 * (CHF) si usa il codice.
 */

export interface CurrencyChoice {
  code: string;
  label: string;
  symbol: string;
}

/** Le regioni che usano una valuta diversa da quella della loro lingua. */
const REGION_CURRENCY: Readonly<Record<string, string>> = {
  US: "USD",
  GB: "GBP",
  CH: "CHF",
  JP: "JPY",
  CN: "CNY",
  CA: "CAD",
  AU: "AUD",
  SE: "SEK",
  NO: "NOK",
  DK: "DKK",
  PL: "PLN",
  CZ: "CZK",
  HU: "HUF",
  RO: "RON",
  BR: "BRL",
  IN: "INR",
  MX: "MXN",
  ZA: "ZAR",
  TR: "TRY",
};

/** La valuta per lingua, quando la regione non ne impone una. */
const LANGUAGE_CURRENCY: Readonly<Record<string, string>> = {
  it: "EUR",
  de: "EUR",
  fr: "EUR",
  es: "EUR",
  pt: "EUR",
  nl: "EUR",
  el: "EUR",
  fi: "EUR",
  sv: "SEK",
  nb: "NOK",
  no: "NOK",
  da: "DKK",
  pl: "PLN",
  cs: "CZK",
  hu: "HUF",
  ro: "RON",
  ja: "JPY",
  zh: "CNY",
  en: "USD",
};

export const CURRENCIES: readonly CurrencyChoice[] = [
  { code: "EUR", label: "Euro", symbol: "€" },
  { code: "USD", label: "Dollaro statunitense", symbol: "$" },
  { code: "GBP", label: "Sterlina britannica", symbol: "£" },
  { code: "CHF", label: "Franco svizzero", symbol: "CHF" },
  { code: "JPY", label: "Yen giapponese", symbol: "¥" },
  { code: "CNY", label: "Yuan cinese", symbol: "¥" },
  { code: "CAD", label: "Dollaro canadese", symbol: "$" },
  { code: "AUD", label: "Dollaro australiano", symbol: "$" },
  { code: "SEK", label: "Corona svedese", symbol: "kr" },
  { code: "NOK", label: "Corona norvegese", symbol: "kr" },
  { code: "DKK", label: "Corona danese", symbol: "kr" },
  { code: "PLN", label: "Zloty polacco", symbol: "zł" },
  { code: "CZK", label: "Corona ceca", symbol: "Kč" },
  { code: "HUF", label: "Fiorino ungherese", symbol: "Ft" },
  { code: "RON", label: "Leu rumeno", symbol: "lei" },
  { code: "BRL", label: "Real brasiliano", symbol: "R$" },
  { code: "INR", label: "Rupia indiana", symbol: "₹" },
  { code: "MXN", label: "Peso messicano", symbol: "$" },
  { code: "ZAR", label: "Rand sudafricano", symbol: "R" },
  { code: "TRY", label: "Lira turca", symbol: "₺" },
];

/**
 * La valuta che il browser dell'utente si aspetta.
 *
 * La regione vince sulla lingua: `en-GB` è sterline, `en-US` dollari, `de-CH`
 * franchi; senza una regione mappata si ricade sulla lingua (`it` → euro). Un
 * codice che non offriamo (o una lingua sconosciuta) ricade su EUR: meglio la
 * valuta dell'area dell'azienda di un codice che il configuratore non mostra.
 */
export function currencyCodeForLocale(locale?: string | null): string {
  const parts = (locale ?? "").trim().split(/[-_]/);
  const language = (parts[0] ?? "").toLowerCase();
  const region =
    parts
      .slice(1)
      .find((part) => /^[A-Za-z]{2}$/.test(part))
      ?.toUpperCase() ?? "";

  const code = REGION_CURRENCY[region] ?? LANGUAGE_CURRENCY[language] ?? "EUR";
  return CURRENCIES.some((currency) => currency.code === code) ? code : "EUR";
}

export const currencyByCode = (code?: string | null): CurrencyChoice | undefined =>
  CURRENCIES.find((currency) => currency.code === code);

/** Il simbolo della valuta, o `null` per un codice che non conosciamo. */
export const symbolFor = (code?: string | null): string | null =>
  currencyByCode(code)?.symbol ?? null;
